import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ClearIcon from '@mui/icons-material/Clear';
import { IconButton, modalClasses, Stack, Tooltip, Typography } from '@mui/material';

import { parseImperialLengthSummationExpression, timeoutAsync } from '@paragon/client-tools';
import { Point2D as Point2DInterface } from '@paragon/contract-library';
import { distanceEquals, LineSegment2D, Point2D, point2DEquals } from '@paragon/geometry-library';
import { DistanceField, FormattedTextFieldHandle, OptionalTooltip } from '@paragon/material';
import { Point3D } from '@paragon/three-shared';
import { getModifierKeys, isInputFocused } from '@paragon/utilities';

import { convertFromDesignToLayoutCoordinates } from '../../../../../../../library/layout-geometry/coordinates';
import {
  angleToDirection,
  CardinalDirection,
  keyboardCodeToDirection,
  movePointInDirection,
} from '../../../../../../../library/layout-geometry/directions';
import { usePointPickingPointSnapper } from '../../../../../../../library/layout-snapping/point-snapping/snapMousePosition';
import {
  BearingEnvelopeInsertionCompletionAction,
  BearingEnvelopeInsertionFinalizingAction,
  finishInsertingBearingEnvelope,
  getBearingEnvelopeInsertionCompletionAction,
} from '../../../../../../../store/actions/layout/bearingEnvelopeActions';
import { getLayoutPointPickingTarget } from '../../../../../../../store/selectors/pickingSelectors';
import { clearPickingObject } from '../../../../../../../store/slices/applicationSlice';
import { BearingEnvelopeInsertionSettings, layoutSlice } from '../../../../../../../store/slices/layoutSlice';
import { threeJsDom } from '../../../../../../../three/util/events/ThreeJSDom';
import { GlobalThreeJSDomObjectEvent } from '../../../../../../../three/util/events/types';
import { DirectionButtons } from '../../../../../../controls/DirectionButtons';
import { useAppDispatch, useAppSelector } from '../../../../../../hooks';
import { BearingEnvelopePropertiesProps } from '../BearingEnvelopeProperties';

const DEFAULT_COMPLETION_ACTION: BearingEnvelopeInsertionFinalizingAction = 'repeat';

type ResolvedLength = number | null | 'invalid';

interface BearingEnvelopeEasyModePropertiesProps extends BearingEnvelopePropertiesProps {
  readonly bearingEnvelopeInsertionSettings: BearingEnvelopeInsertionSettings;
}

function isNumericDistanceStartingKey(event: KeyboardEvent) {
  return (
    !event.ctrlKey && !event.metaKey && !event.altKey && event.key.length === 1 && event.key >= '0' && event.key <= '9'
  );
}

function isKeyEventFromModal(event: KeyboardEvent) {
  return event.target instanceof Element && event.target.closest(`.${modalClasses.root}`) != null;
}

function getDirectedEndpoint(
  basePoint: Point2DInterface,
  endpointToSet: 'left' | 'right',
  direction: CardinalDirection,
  length: number,
) {
  const signedLength = endpointToSet === 'left' ? -length : length;
  return movePointInDirection(basePoint, { direction, distance: signedLength });
}

interface MousePositionForFixedPoint {
  readonly fixedPointKey: string;
  readonly position: Point3D;
}

function useLivePreviewSegment(properties: BearingEnvelopePropertiesProps['properties']) {
  const pickingTarget = useAppSelector(getLayoutPointPickingTarget);
  const snapMousePosition = usePointPickingPointSnapper();

  const isLeftFixed = pickingTarget === 'bearing-envelope-right' && properties.leftPoint != null;
  const isRightFixed = pickingTarget === 'bearing-envelope-left' && properties.rightPoint != null;
  const fixedPoint = isLeftFixed ? properties.leftPoint : isRightFixed ? properties.rightPoint : null;
  const fixedPointKey = fixedPoint ? `${pickingTarget}:${fixedPoint.x}:${fixedPoint.y}` : null;

  const [mousePositionForFixedPoint, setMousePositionForFixedPoint] = useState<MousePositionForFixedPoint | null>(null);

  useEffect(() => {
    if (fixedPointKey == null) {
      setMousePositionForFixedPoint(null);
      return;
    }

    const currentFixedPointKey = fixedPointKey;

    function recordMousePositionForCurrentFixedPoint(event: GlobalThreeJSDomObjectEvent) {
      const { worldPosition } = event.eventPosition;
      setMousePositionForFixedPoint((prev) =>
        prev?.fixedPointKey === currentFixedPointKey &&
        prev.position.x === worldPosition.x &&
        prev.position.y === worldPosition.y &&
        prev.position.z === worldPosition.z
          ? prev
          : { fixedPointKey: currentFixedPointKey, position: worldPosition },
      );
    }

    function clearMousePosition() {
      setMousePositionForFixedPoint(null);
    }

    threeJsDom.addGlobalListener('mousemove', recordMousePositionForCurrentFixedPoint);
    threeJsDom.addGlobalListener('mouseleave', clearMousePosition);

    return () => {
      threeJsDom.removeGlobalListener('mousemove', recordMousePositionForCurrentFixedPoint);
      threeJsDom.removeGlobalListener('mouseleave', clearMousePosition);
    };
  }, [fixedPointKey]);

  return useMemo(() => {
    if (fixedPoint == null) return null;
    if (mousePositionForFixedPoint?.fixedPointKey !== fixedPointKey) return null;

    const snappedPoint = snapMousePosition(
      convertFromDesignToLayoutCoordinates(mousePositionForFixedPoint.position).projectTo2D(),
    ).point;

    const fixed = Point2D.fromObject(fixedPoint);
    if (fixed.equals(snappedPoint)) return null;

    return isLeftFixed ? new LineSegment2D(fixed, snappedPoint) : new LineSegment2D(snappedPoint, fixed);
  }, [fixedPoint, fixedPointKey, isLeftFixed, mousePositionForFixedPoint, snapMousePosition]);
}

export function BearingEnvelopeEasyModeProperties(props: BearingEnvelopeEasyModePropertiesProps) {
  const dispatch = useAppDispatch();

  const [editingLength, setEditingLength] = useState<number | null>(null);

  const lengthInputRef = useRef<HTMLInputElement>(null);
  const lengthFieldRef = useRef<FormattedTextFieldHandle>(null);

  const bearingType = props.properties.bearingType;
  const {
    lockedLength,
    lockedDirection: desiredDirection,
    autoInsertOnSecondPoint,
  } = props.bearingEnvelopeInsertionSettings;

  const segment = useMemo(
    () =>
      props.properties.leftPoint &&
      props.properties.rightPoint &&
      !point2DEquals(props.properties.leftPoint, props.properties.rightPoint)
        ? LineSegment2D.fromPointObjects(props.properties.leftPoint, props.properties.rightPoint)
        : null,
    [props.properties.leftPoint, props.properties.rightPoint],
  );
  const livePreviewSegment = useLivePreviewSegment(props.properties);

  const finishInserting = useCallback(
    async (action: BearingEnvelopeInsertionFinalizingAction) => {
      await dispatch(
        finishInsertingBearingEnvelope(action === 'repeat' ? { postInsertionAction: 'repeat' } : undefined),
      );

      setEditingLength(null);
    },
    [dispatch],
  );

  const effectiveLength = lockedLength ?? editingLength ?? livePreviewSegment?.length ?? segment?.length ?? null;
  const effectiveLengthRef = useRef<number | null>(null);
  effectiveLengthRef.current = effectiveLength;

  const direction = segment ? angleToDirection(segment.direction.angle) : (desiredDirection ?? null);

  useEffect(() => {
    if (lockedLength == null && (props.properties.leftPoint == null || props.properties.rightPoint == null)) {
      setEditingLength(null);
    }
  }, [lockedLength, props.properties.leftPoint, props.properties.rightPoint]);

  const startPointMissing = props.properties.leftPoint == null;

  function applyDirectedEndpoint(direction: CardinalDirection, length: number): boolean {
    if (props.properties.leftPoint) {
      const rightPoint = getDirectedEndpoint(props.properties.leftPoint, 'right', direction, length);
      props.onChange({ rightPoint });
      return true;
    }

    if (props.properties.rightPoint) {
      const leftPoint = getDirectedEndpoint(props.properties.rightPoint, 'left', direction, length);
      props.onChange({ leftPoint });
      return true;
    }

    return false;
  }

  async function setDirection(
    desiredDirection: CardinalDirection,
    completionAction: BearingEnvelopeInsertionCompletionAction,
    lengthToUse: number | null,
  ) {
    if (startPointMissing) return;

    if (lengthToUse == null || !applyDirectedEndpoint(desiredDirection, lengthToUse)) {
      dispatch(
        layoutSlice.actions.bearingEnvelopeInsertionLockedDirectionSet({
          bearingType,
          lockedDirection: desiredDirection,
        }),
      );
      return;
    }

    dispatch(clearPickingObject());

    if (completionAction != null) {
      await finishInserting(completionAction);
    }
  }

  const setDirectionRef = useRef(setDirection);
  setDirectionRef.current = setDirection;

  const resolveAndLockLengthForDirectionShortcut = useCallback((): ResolvedLength => {
    const input = lengthInputRef.current;
    const displayedLength = input?.value.trim() ?? '';

    let length: number | null;
    if (displayedLength !== '') {
      const parsed = parseImperialLengthSummationExpression(displayedLength);
      if (parsed == null) return 'invalid';
      length = parsed;
    } else if (input != null && document.activeElement === input) {
      length = null;
    } else {
      length = effectiveLengthRef.current;
    }

    if (length != null) {
      dispatch(layoutSlice.actions.bearingEnvelopeInsertionLockedLengthSet({ bearingType, lockedLength: length }));
    }
    return length;
  }, [bearingType, dispatch]);

  useEffect(() => {
    async function applyDirectionShortcut(event: KeyboardEvent) {
      const direction = keyboardCodeToDirection(event.code);

      if (direction == null) return;
      if (isKeyEventFromModal(event)) return;
      if (startPointMissing) return;
      if (lengthInputRef.current == null) return;
      if (isInputFocused() && document.activeElement !== lengthInputRef.current) return;

      event.preventDefault();

      const resolvedLength = resolveAndLockLengthForDirectionShortcut();
      if (resolvedLength === 'invalid') return;

      lengthInputRef.current.focus();
      lengthInputRef.current.blur();
      await timeoutAsync(); // wait for blur

      await setDirectionRef.current(
        direction,
        getBearingEnvelopeInsertionCompletionAction(
          getModifierKeys(event),
          autoInsertOnSecondPoint ? DEFAULT_COMPLETION_ACTION : null,
        ),
        resolvedLength,
      );
    }

    async function onGlobalKeyDown(event: KeyboardEvent) {
      if (lengthInputRef.current == null || event.code.startsWith('Arrow')) return;
      if (isKeyEventFromModal(event)) return;

      if (!isInputFocused() && isNumericDistanceStartingKey(event)) {
        event.preventDefault();

        lengthInputRef.current.focus();
        lengthFieldRef.current?.setDisplayValue(event.key);

        return;
      }

      if (event.key === 'Enter') {
        if (isInputFocused()) return;

        event.preventDefault();

        // always finalize on enter
        const completionAction = getBearingEnvelopeInsertionCompletionAction(
          getModifierKeys(event),
          DEFAULT_COMPLETION_ACTION,
        );

        if (completionAction == null) {
          throw new Error('Unexpected non-finalizing enter keypress.');
        }

        await finishInserting(completionAction);

        return;
      }
    }

    document.addEventListener('keydown', applyDirectionShortcut, true);
    document.addEventListener('keydown', onGlobalKeyDown);
    return () => {
      document.removeEventListener('keydown', applyDirectionShortcut, true);
      document.removeEventListener('keydown', onGlobalKeyDown);
    };
  }, [autoInsertOnSecondPoint, finishInserting, resolveAndLockLengthForDirectionShortcut, startPointMissing]);

  return (
    <>
      <OptionalTooltip visible={startPointMissing} title="Available once start point chosen">
        <Stack direction="row" sx={{ alignItems: 'center' }} spacing={1}>
          <DistanceField
            label="Length"
            canBeNull
            disabled={startPointMissing}
            ref={lengthFieldRef}
            inputRef={lengthInputRef}
            value={effectiveLength}
            endAdornment={
              lockedLength != null ? (
                <Tooltip key="clear-locked-length" disableInteractive title="Clear locked length">
                  <IconButton
                    aria-label="Clear locked length"
                    edge="end"
                    size="small"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => {
                      dispatch(
                        layoutSlice.actions.bearingEnvelopeInsertionLockedLengthSet({
                          bearingType,
                          lockedLength: null,
                        }),
                      );
                      setEditingLength(null);
                    }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : undefined
            }
            onFocus={() => {
              setEditingLength(effectiveLength);
            }}
            onBlur={() => {
              setEditingLength(null);
            }}
            onKeyDown={(e) => {
              if (e.code === 'Escape') lengthInputRef.current?.blur();
            }}
            onSubmit={(length, { event, finalizing }) => {
              dispatch(
                layoutSlice.actions.bearingEnvelopeInsertionLockedLengthSet({ bearingType, lockedLength: length }),
              );

              // if already fully defined, we just edit the length
              if (segment) {
                if (!distanceEquals(segment.length, length)) {
                  props.onChange({ rightPoint: segment.toLine().getPointAlongLine(length).toObject() });
                }
              }

              // if explicit Enter and we already have a direction, re-affirm that direction so insertion can proceed
              else if (finalizing && desiredDirection != null) {
                void setDirection(
                  desiredDirection,
                  getBearingEnvelopeInsertionCompletionAction(
                    getModifierKeys(event.nativeEvent as KeyboardEvent), // event is KeyboardEvent because we are finalizing
                    autoInsertOnSecondPoint ? DEFAULT_COMPLETION_ACTION : null,
                  ),
                  length,
                );
              }
            }}
          />

          <DirectionButtons
            value={direction}
            disabled={startPointMissing}
            onChange={async (event, direction) => {
              const resolvedLength = resolveAndLockLengthForDirectionShortcut();
              if (resolvedLength === 'invalid') return;

              await setDirection(
                direction,
                getBearingEnvelopeInsertionCompletionAction(
                  getModifierKeys(event.nativeEvent),
                  autoInsertOnSecondPoint ? DEFAULT_COMPLETION_ACTION : null,
                ),
                resolvedLength,
              );
            }}
          />
        </Stack>
      </OptionalTooltip>

      <Typography variant="body2" style={{ whiteSpace: 'normal' }}>
        Instead of picking an end point, you can also type a distance and direction to define it quickly.
      </Typography>
    </>
  );
}
